// src/pages/Users/Galleries/components/AlbumDetailHero.jsx

import React, { useMemo } from "react";
import createDOMPurify from "dompurify";
import {
  FaCalendarAlt,
  FaEye,
  FaImages,
  FaStar,
} from "react-icons/fa";

import {
  countPhotos,
  getCoverUrl,
  stripHtml,
} from "../utils/galleriesUtils";

const FALLBACK = "/img/no-image.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi sur fond sombre
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "album-detail-hero-rich-content",
  "prose prose-invert max-w-none break-words",
  "text-sm leading-7 text-slate-200 sm:text-base",
  "prose-p:text-slate-200",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:!text-white",
  "prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-orange-300",
  "hover:prose-a:underline",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-orange-400",
  "prose-blockquote:bg-white/10",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:text-slate-200",
  "prose-blockquote:not-italic",

  // Images intégrées
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-lg",

  // Séparateurs et code
  "prose-hr:border-white/20",
  "prose-code:break-words",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

function onImgError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK;
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.valueOf())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isCoverPhoto(photo) {
  return (
    photo?.is_cover === true ||
    photo?.is_cover === 1 ||
    photo?.is_cover === "1"
  );
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function AlbumDetailHero({
  gallery,
  photos = [],
}) {
  const coverUrl = getCoverUrl(gallery);

  const descriptionHtml = useMemo(() => {
    const rawDescription = gallery?.description || "";
    const plainDescription = stripHtml(rawDescription);

    if (!plainDescription) {
      return sanitizeHtml(
        "<p><em>Album photo du club.</em></p>",
      );
    }

    return sanitizeHtml(rawDescription);
  }, [gallery?.description]);

  const coverPhoto = photos.find(isCoverPhoto);

  return (
    <section className="relative overflow-hidden rounded-[2rem] bg-slate-950 text-white shadow-xl shadow-slate-950/10">
      {/* Image de fond */}

      <div className="absolute inset-0">
        <img
          src={coverUrl}
          alt={gallery?.titre || "Album photo"}
          className="h-full w-full object-cover opacity-45"
          loading="eager"
          onError={onImgError}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/35" />
      </div>

      {/* Contenu */}

      <div className="relative p-5 sm:p-6 lg:p-8">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white ring-1 ring-white/20">
            <FaImages />
            Album photos
          </p>

          <h2 className="mt-4 text-3xl font-bold leading-tight sm:text-4xl">
            {gallery?.titre || "Album sans titre"}
          </h2>

          {/* Description HTML enrichie */}

          <div
            className={cn(
              RICH_TEXT_CLASSES,
              "mt-4 max-h-[220px] overflow-y-auto overflow-x-hidden pr-3",
              "scrollbar-thin scrollbar-track-transparent",
              "scrollbar-thumb-white/25",
            )}
            style={{
              tabSize: 4,
              WebkitOverflowScrolling: "touch",
            }}
            dangerouslySetInnerHTML={{
              __html: descriptionHtml,
            }}
          />
        </div>

        {/* Statistiques */}

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <HeroStat
            icon={FaImages}
            label="Photos"
            value={countPhotos(gallery)}
          />

          <HeroStat
            icon={FaEye}
            label="Statut"
            value={
              gallery?.is_active
                ? "Visible"
                : "Masqué"
            }
          />

          <HeroStat
            icon={FaStar}
            label="Couverture"
            value={coverPhoto ? "OK" : "Auto"}
          />

          <HeroStat
            icon={FaCalendarAlt}
            label="Mise à jour"
            value={formatDate(
              gallery?.updated_at ||
                gallery?.created_at,
            )}
          />
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------
   Statistique du Hero
-------------------------------------------------------- */

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-300">
        <Icon />
        {label}
      </div>

      <p className="mt-2 text-lg font-bold text-white">
        {value}
      </p>
    </div>
  );
}